import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import HelpIcon from './HelpIcon';
import FeatureNotice from './FeatureNotice';
import { getHelpContent } from './helpContent';
import { todayISO } from '../utils/dates';

import DateInput from './DateInput';
import { formatDate } from '../utils/dateFormat';
/**
 * Quality Check module — inspect RECEIVED lots before they become issuable.
 * Measurements (e.g. Brix / Moisture) auto-select org quality grades when rules match.
 */
function Quality() {
  const [loading, setLoading] = useState(true);
  const [pendingLots, setPendingLots] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [showInspectModal, setShowInspectModal] = useState(false);
  const [selectedLot, setSelectedLot] = useState(null);
  const [grades, setGrades] = useState([]);
  const [measurementFields, setMeasurementFields] = useState([]);
  const [gradeSource, setGradeSource] = useState('NONE');
  const [autoGradeLabel, setAutoGradeLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [showHelpPane, setShowHelpPane] = useState(false);
  const [helpContent, setHelpContent] = useState(null);
  const [form, setForm] = useState({
    result: 'PASS',
    passedQuantity: '',
    rejectedQuantity: '',
    qualityGradeId: '',
    disposition: 'ACCEPT',
    remarks: '',
    inspectionDate: todayISO(),
    measurements: {},
  });

  const fetchPending = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (warehouseFilter) params.warehouseId = warehouseFilter;
      const res = await axios.get('/api/quality/pending-lots', { params });
      setPendingLots(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      toast.error(error.response?.data || 'Failed to load pending QC lots');
      setPendingLots([]);
    } finally {
      setLoading(false);
    }
  }, [warehouseFilter]);

  const fetchWarehouses = useCallback(async () => {
    try {
      const res = await axios.get('/api/inventory/warehouses');
      setWarehouses(Array.isArray(res.data) ? res.data : []);
    } catch {
      setWarehouses([]);
    }
  }, []);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const gradeScopeForLot = (lot) => (
    lot?.inspectionType === 'PRODUCTION' ? 'PRODUCT' : 'MATERIAL'
  );

  const resolveAutoGrade = async (itemTypeId, itemId, scope, measurements) => {
    if (!itemTypeId) return;
    const hasValues = Object.values(measurements || {}).some((v) => v !== '' && v != null);
    if (!hasValues) {
      setAutoGradeLabel('');
      return;
    }
    try {
      const res = await axios.post(`/api/quality/item-types/${itemTypeId}/resolve-grade`, {
        scope,
        measurements,
      }, { params: itemId ? { itemId } : {} });
      const grade = res.data?.grade;
      if (grade) {
        setAutoGradeLabel(`${grade.code} — ${grade.label}`);
        setForm((prev) => ({ ...prev, qualityGradeId: String(grade.id) }));
      } else {
        setAutoGradeLabel('No matching grade for these measurements');
      }
    } catch {
      setAutoGradeLabel('');
    }
  };

  const openInspect = async (lot) => {
    setSelectedLot(lot);
    setForm({
      result: 'PASS',
      passedQuantity: String(lot.pendingQuantity ?? lot.receivedQuantity ?? ''),
      rejectedQuantity: '',
      qualityGradeId: '',
      disposition: 'ACCEPT',
      remarks: '',
      inspectionDate: todayISO(),
      measurements: {},
    });
    setGrades([]);
    setMeasurementFields([]);
    setGradeSource('NONE');
    setAutoGradeLabel('');
    if (lot.itemTypeId) {
      try {
        const scope = gradeScopeForLot(lot);
        const res = await axios.get(`/api/quality/item-types/${lot.itemTypeId}/grades`, {
          params: { scope, itemId: lot.itemId || undefined },
        });
        const data = res.data || {};
        const list = Array.isArray(data.grades) ? data.grades
          : (Array.isArray(res.data) ? res.data : []);
        const fields = Array.isArray(data.measurementFields) ? data.measurementFields : [];
        setGrades(list);
        setMeasurementFields(fields);
        setGradeSource(data.source || 'NONE');
        const measurements = {};
        fields.forEach((f) => { measurements[f.code] = ''; });
        setForm((prev) => ({ ...prev, measurements }));
      } catch {
        setGrades([]);
        setMeasurementFields([]);
      }
    }
    setShowInspectModal(true);
  };

  const onMeasurementChange = (code, value) => {
    setForm((prev) => {
      const measurements = { ...prev.measurements, [code]: value };
      return { ...prev, measurements };
    });
  };

  useEffect(() => {
    if (!showInspectModal || !selectedLot?.itemTypeId) return;
    const t = setTimeout(() => {
      resolveAutoGrade(
        selectedLot.itemTypeId,
        selectedLot.itemId,
        gradeScopeForLot(selectedLot),
        form.measurements
      );
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.measurements, showInspectModal, selectedLot]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLot) return;
    try {
      setSaving(true);
      const measurements = {};
      Object.entries(form.measurements || {}).forEach(([k, v]) => {
        if (v !== '' && v != null) measurements[k] = parseFloat(v);
      });
      const payload = {
        grnLineItemId: selectedLot.lotId,
        result: form.result,
        passedQuantity: form.passedQuantity !== '' ? parseFloat(form.passedQuantity) : null,
        rejectedQuantity: form.rejectedQuantity !== '' ? parseFloat(form.rejectedQuantity) : null,
        qualityGradeId: form.qualityGradeId || null,
        disposition: form.result === 'FAIL' ? form.disposition : (form.disposition || 'ACCEPT'),
        remarks: form.remarks || null,
        inspectionDate: form.inspectionDate || null,
        measurements,
      };
      await axios.post('/api/quality/inspections', payload);
      toast.success('Quality inspection recorded');
      setShowInspectModal(false);
      setSelectedLot(null);
      fetchPending();
    } catch (error) {
      const msg = error.response?.data || 'Failed to record inspection';
      toast.error(typeof msg === 'string' ? msg : 'Failed to record inspection');
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = (status) => {
    const s = (status || '').toUpperCase();
    if (s === 'PENDING_QC') return <Badge bg="warning" text="dark">Pending QC</Badge>;
    if (s === 'PARTIAL') return <Badge bg="info">Partial</Badge>;
    if (s === 'FAILED') return <Badge bg="danger">Failed</Badge>;
    if (s === 'PASSED') return <Badge bg="success">Passed</Badge>;
    return <Badge bg="secondary">{status || '-'}</Badge>;
  };

  return (
    <div className="container-fluid py-3">
      <FeatureNotice feature="QUALITY_CONTROL" className="mb-3" />
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">
            Quality Check{' '}
            <HelpIcon
              onClick={() => {
                const content = getHelpContent('quality');
                if (content) {
                  setHelpContent(content);
                  setShowHelpPane(true);
                }
              }}
            />
          </h4>
          <p className="text-muted mb-0">
            Inspect received lots. Enter measurements to auto-calculate grade. Stock is not issuable until QC passes.
          </p>
        </div>
        <Button variant="outline-secondary" onClick={fetchPending} disabled={loading}>
          Refresh
        </Button>
      </div>

      <Card className="mb-3">
        <Card.Body>
          <Row className="align-items-end">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Warehouse filter</Form.Label>
                <Form.Select
                  value={warehouseFilter}
                  onChange={(e) => setWarehouseFilter(e.target.value)}
                >
                  <option value="">All warehouses / locations</option>
                  {warehouses.filter((w) => w.isActive !== false).map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      ) : pendingLots.length === 0 ? (
        <Alert variant="success" className="mb-0">
          No lots pending quality check. Received material is either fully passed or not yet received.
        </Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Lot</th>
              <th>Item</th>
              <th>GRN</th>
              <th>Location</th>
              <th>Type</th>
              <th>Received</th>
              <th>Passed</th>
              <th>Pending</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pendingLots.map((lot) => (
              <tr key={lot.lotId}>
                <td>
                  <strong>{lot.internalLotNumber || `Lot ${lot.lotId}`}</strong>
                  {lot.vendorLotNumber ? (
                    <div className="small text-muted">Vendor: {lot.vendorLotNumber}</div>
                  ) : null}
                </td>
                <td>{lot.itemName}</td>
                <td>
                  {lot.grnNumber}
                  {lot.grnDate ? <div className="small text-muted">{formatDate(lot.grnDate)}</div> : null}
                </td>
                <td>{lot.warehouseName || lot.productionUnitName || '-'}</td>
                <td>{lot.inspectionType === 'PRODUCTION' ? 'FG / Production' : 'Incoming'}</td>
                <td>{lot.receivedQuantity}{lot.uomSymbol ? ` ${lot.uomSymbol}` : ''}</td>
                <td>{lot.qcPassedQuantity}</td>
                <td><strong>{lot.pendingQuantity}</strong></td>
                <td>{statusBadge(lot.qcStatus)}</td>
                <td>
                  <Button size="sm" variant="primary" onClick={() => openInspect(lot)}>
                    Inspect
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showInspectModal} onHide={() => setShowInspectModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Inspect lot {selectedLot?.internalLotNumber || selectedLot?.lotId}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {selectedLot && (
              <Alert variant="light" className="border mb-3">
                <div><strong>Item:</strong> {selectedLot.itemName}</div>
                <div><strong>GRN:</strong> {selectedLot.grnNumber}</div>
                <div>
                  <strong>Pending qty:</strong> {selectedLot.pendingQuantity}
                  {selectedLot.uomSymbol ? ` ${selectedLot.uomSymbol}` : ''}
                </div>
              </Alert>
            )}

            {measurementFields.length > 0 && (
              <>
                <h6 className="mb-2">Measurements</h6>
                <Row>
                  {measurementFields.map((field) => (
                    <Col md={4} key={field.code}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          {field.name || field.code}
                          {field.unit ? ` (${field.unit})` : ''}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="any"
                          value={form.measurements?.[field.code] ?? ''}
                          onChange={(e) => onMeasurementChange(field.code, e.target.value)}
                          placeholder="e.g. 19"
                        />
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
                {autoGradeLabel && (
                  <Alert variant={autoGradeLabel.startsWith('No matching') ? 'warning' : 'success'} className="py-2">
                    <strong>Auto grade:</strong> {autoGradeLabel}
                  </Alert>
                )}
              </>
            )}

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Result *</Form.Label>
                  <Form.Select
                    value={form.result}
                    onChange={(e) => {
                      const result = e.target.value;
                      setForm((prev) => ({
                        ...prev,
                        result,
                        disposition: result === 'FAIL' ? 'HOLD' : 'ACCEPT',
                        passedQuantity:
                          result === 'FAIL'
                            ? '0'
                            : String(selectedLot?.pendingQuantity ?? selectedLot?.receivedQuantity ?? ''),
                      }));
                    }}
                    required
                  >
                    <option value="PASS">Pass (clear pending qty)</option>
                    <option value="PARTIAL">Partial pass</option>
                    <option value="FAIL">Fail</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Inspection date</Form.Label>
                  <DateInput
                   
                    value={form.inspectionDate}
                    onChange={(e) => setForm({ ...form, inspectionDate: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Grade{' '}
                    {gradeSource === 'ITEM' && <Badge bg="primary" className="ms-1">Item</Badge>}
                    {gradeSource === 'ORGANIZATION' && <Badge bg="primary" className="ms-1">Org</Badge>}
                    {gradeSource === 'SYSTEM' && <Badge bg="secondary" className="ms-1">System</Badge>}
                  </Form.Label>
                  <Form.Select
                    value={form.qualityGradeId}
                    onChange={(e) => setForm({ ...form, qualityGradeId: e.target.value })}
                    disabled={grades.length === 0}
                  >
                    <option value="">
                      {grades.length
                        ? (measurementFields.length ? 'Auto / select grade' : 'Select grade (optional)')
                        : 'No grades — define in Org Admin → Quality Grades'}
                    </option>
                    {grades.map((g) => (
                      <option key={g.id} value={g.id}>{g.code} — {g.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Passed qty {form.result === 'PARTIAL' ? '*' : ''}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.001"
                    min="0"
                    value={form.passedQuantity}
                    onChange={(e) => setForm({ ...form, passedQuantity: e.target.value })}
                    disabled={form.result === 'FAIL'}
                    required={form.result === 'PARTIAL'}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Rejected qty</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.001"
                    min="0"
                    value={form.rejectedQuantity}
                    onChange={(e) => setForm({ ...form, rejectedQuantity: e.target.value })}
                    disabled={form.result === 'PASS'}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Disposition {form.result === 'FAIL' ? '*' : ''}</Form.Label>
                  <Form.Select
                    value={form.disposition}
                    onChange={(e) => setForm({ ...form, disposition: e.target.value })}
                    required={form.result === 'FAIL'}
                  >
                    {form.result === 'FAIL' ? (
                      <>
                        <option value="HOLD">Hold</option>
                        <option value="SCRAP">Scrap</option>
                      </>
                    ) : (
                      <option value="ACCEPT">Accept</option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-0">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowInspectModal(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Record inspection'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {showHelpPane && helpContent && (
        <div className="help-pane border-start p-3" style={{ position: 'fixed', right: 0, top: 0, bottom: 0, width: 360, background: 'var(--bs-body-bg)', zIndex: 1050, overflow: 'auto' }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">{helpContent.title || 'Help'}</h5>
            <Button size="sm" variant="outline-secondary" onClick={() => setShowHelpPane(false)}>Close</Button>
          </div>
          <div>{helpContent.content || helpContent.body}</div>
        </div>
      )}
    </div>
  );
}

export default Quality;
